// React / Next
import { useState, FormEvent } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { SEO } from '../../components/SEO/SEO';
import { CTAButton } from '../../components/ui/CTAButton/CTAButton';

// Hooks
import {
  useHubSpotForm,
  HUBSPOT_PARTNER_CONFIG,
} from '../../hooks/useHubSpotForm';
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Utils
import { isPersonalEmail } from '../../utils/emailValidation';

// Types
type FormData = {
  firstName: string;
  lastName: string;
  workEmail: string;
  phoneNumber: string;
  country: string;
  jobTitle: string;
  firmWebsite: string;
  firmSize: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

const COUNTRY_OPTIONS = [
  { value: '', label: '' },
  { value: 'USA', label: 'USA' },
  { value: 'UK', label: 'UK' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
];

const FIRM_SIZE_OPTIONS = [
  { value: '', label: '' },
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-100', label: '51-100 employees' },
  { value: '101-250', label: '101-250 employees' },
  { value: '251-500', label: '251-500 employees' },
  { value: '500+', label: '500+ employees' },
];

/**
 * PartnerPage - Design Partner application form page
 * Allows firms to apply to become design partners for Archie
 */
export const PartnerPage = () => {
  const trackEvent = useTrackEvent();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    workEmail: '',
    phoneNumber: '',
    country: '',
    jobTitle: '',
    firmWebsite: '',
    firmSize: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // HubSpot form submission hook
  const {
    submit: submitToHubSpot,
    isSubmitting,
    isSuccess: isSubmitted,
    error: hubSpotError,
  } = useHubSpotForm(HUBSPOT_PARTNER_CONFIG);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    } else if (isPersonalEmail(formData.workEmail)) {
      newErrors.workEmail = 'Please use your work email address.';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.firmWebsite.trim()) {
      newErrors.firmWebsite = 'Firm website is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Submit to HubSpot with mapped field names
    const success = await submitToHubSpot([
      { name: 'firstname', value: formData.firstName },
      { name: 'lastname', value: formData.lastName },
      { name: 'email', value: formData.workEmail },
      { name: 'mobilephone', value: formData.phoneNumber },
      { name: 'country', value: formData.country },
      { name: 'jobtitle', value: formData.jobTitle },
      { name: 'website', value: formData.firmWebsite },
      { name: 'company_size', value: formData.firmSize },
    ]);

    if (success) {
      trackEvent('form_submitted', {
        form_name: 'partner',
        form_location: 'partner_page',
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center bg-white min-h-[calc(100vh-72px)]">
        <SEO
          title="Thank You | Partner With Us"
          description="Thank you for your interest in becoming a design partner with Archie. Our team will review your details and be in touch shortly."
          canonicalUrl="/partner"
        />

        <div className="flex flex-col items-center justify-center gap-[var(--spacing-24)] px-[var(--spacing-24)] py-[var(--spacing-48)] max-w-[600px] text-center">
          <div className="w-16 h-16 rounded-full bg-green-1000/20 flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-green-1000"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000">
            Thank you for your interest!
          </h1>

          <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
            Our team will review your details and be in touch shortly.
          </p>

          <Link
            to="/"
            className="font-body text-[16px] leading-[24px] text-orange-800 hover:text-orange-1000 underline underline-offset-4 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center bg-white min-h-[calc(100vh-72px)]">
      <SEO
        title="Become a Design Partner"
        description="Join the firms helping define how AI actually works inside modern accounting teams. Partner with Archie to shape the future of AI in accounting."
        canonicalUrl="/partner"
      />

      {/* Centered Content Wrapper */}
      <div className="flex items-center justify-center w-full flex-1 py-[var(--spacing-32)]">
        {/* Main Content */}
        <section className="flex flex-col lg:flex-row lg:items-start gap-[var(--spacing-64)] px-[var(--spacing-24)] w-full max-w-[1224px] max-lg:gap-[var(--spacing-48)] max-md:px-[var(--spacing-16)]">
          {/* Left Column - Text */}
          <div className="flex flex-col gap-[var(--spacing-24)] lg:w-[392px] lg:shrink-0">
            <h1 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000">
              Become a Design Partner
            </h1>

            <div className="flex flex-col gap-[var(--spacing-16)]">
              <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
                This partnership isn't about a pilot or a product test. It's a
                collaboration.
              </p>
              <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
                Join the firms helping define how AI actually works inside
                modern accounting teams.
              </p>
              <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
                Our team will review your details and be in touch shortly.
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-[var(--spacing-24)]"
              noValidate
            >
              {/* Name Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--spacing-16)]">
                <InputField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(value) => handleInputChange('firstName', value)}
                  error={errors.firstName}
                  required
                  autoComplete="given-name"
                />
                <InputField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(value) => handleInputChange('lastName', value)}
                  error={errors.lastName}
                  required
                  autoComplete="family-name"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--spacing-16)]">
                <InputField
                  label="Work Email"
                  type="email"
                  value={formData.workEmail}
                  onChange={(value) => handleInputChange('workEmail', value)}
                  error={errors.workEmail}
                  required
                  autoComplete="email"
                />
                <InputField
                  label="Phone Number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(value) => handleInputChange('phoneNumber', value)}
                  error={errors.phoneNumber}
                  required
                  autoComplete="tel"
                />
              </div>

              {/* Country - Full Width Dropdown */}
              <SelectField
                label="Country"
                value={formData.country}
                onChange={(value) => handleInputChange('country', value)}
                options={COUNTRY_OPTIONS}
                error={errors.country}
                required
              />

              {/* Job Title - Full Width */}
              <InputField
                label="Job Title"
                value={formData.jobTitle}
                onChange={(value) => handleInputChange('jobTitle', value)}
                error={errors.jobTitle}
                required
                autoComplete="organization-title"
              />

              {/* Firm Website - Full Width */}
              <InputField
                label="Firm Website"
                type="url"
                value={formData.firmWebsite}
                onChange={(value) => handleInputChange('firmWebsite', value)}
                error={errors.firmWebsite}
                required
                autoComplete="url"
                placeholder="https://"
              />

              {/* Firm Size - Full Width Dropdown */}
              <SelectField
                label="Firm Size"
                value={formData.firmSize}
                onChange={(value) => handleInputChange('firmSize', value)}
                options={FIRM_SIZE_OPTIONS}
                error={errors.firmSize}
              />

              {/* Terms Agreement */}
              <p className="font-body text-[16px] leading-[24px] text-blue-1000 text-center">
                By registering you agree to Archie's{' '}
                <Link
                  to="/terms-of-service"
                  className="underline underline-offset-2 hover:text-orange-800 transition-colors"
                >
                  terms
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 hover:text-orange-800 transition-colors"
                >
                  policy
                </Link>
                .
              </p>

              {/* HubSpot Error Message */}
              {hubSpotError && (
                <p
                  className="font-body text-[14px] leading-[20px] text-red-1000 text-center"
                  role="alert"
                >
                  {hubSpotError}
                </p>
              )}

              {/* Submit Button */}
              <div className="flex justify-center">
                <CTAButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send'}
                </CTAButton>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

// Types
type InputFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
};

/**
 * InputField - Reusable form input component matching Figma design
 */
const InputField = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  required = false,
  autoComplete,
  placeholder,
}: InputFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.length > 0;

  return (
    <div className="flex flex-col gap-[var(--spacing-4)]">
      <div
        className={`
          relative h-[56px]
          px-[var(--spacing-16)]
          border rounded-[var(--radius-8)]
          transition-colors duration-200
          ${error ? 'border-red-1000' : isFocused ? 'border-orange-800' : 'border-blue-1000'}
        `}
      >
        {/* Label - hidden when input has value */}
        {!hasValue && (
          <label className="absolute left-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none font-body text-[16px] leading-[24px] text-blue-1000">
            {label}
            {required && <span className="text-red-1000 ml-0.5">*</span>}
          </label>
        )}

        {/* Input */}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className="absolute inset-0 w-full h-full px-[var(--spacing-16)] bg-transparent outline-none font-body text-[16px] leading-[24px] text-blue-1000 placeholder:text-transparent"
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${label}-error` : undefined}
        />
      </div>

      {/* Error Message */}
      {error && (
        <p
          id={`${label}-error`}
          className="font-body text-[12px] leading-[16px] text-red-1000"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

// Types
type SelectFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  error?: string;
  required?: boolean;
};

/**
 * SelectField - Form select/dropdown component matching the light theme
 */
const SelectField = ({
  label,
  value,
  onChange,
  options,
  error,
  required = false,
}: SelectFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.length > 0;

  return (
    <div className="flex flex-col gap-[var(--spacing-4)]">
      <div
        className={`
          relative h-[56px]
          px-[var(--spacing-16)]
          border rounded-[var(--radius-8)]
          transition-colors duration-200
          ${error ? 'border-red-1000' : isFocused ? 'border-orange-800' : 'border-blue-1000'}
        `}
      >
        {/* Label - hidden when select has value */}
        {!hasValue && (
          <label className="absolute left-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none font-body text-[16px] leading-[24px] text-blue-1000">
            {label}
            {required && <span className="text-red-1000 ml-0.5">*</span>}
          </label>
        )}

        {/* Select */}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="absolute inset-0 w-full h-full px-[var(--spacing-16)] pr-[var(--spacing-40)] bg-transparent outline-none font-body text-[16px] leading-[24px] text-blue-1000 appearance-none cursor-pointer"
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${label}-error` : undefined}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Dropdown Arrow */}
        <div className="absolute right-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="#0A2540"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p
          id={`${label}-error`}
          className="font-body text-[12px] leading-[16px] text-red-1000"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};
